import * as T from 'three';
import type {CrystalWingFlightFrame} from './crystalWings';

const LANDING_BONES=['pelvis','spine_01','spine_02','spine_03','spine_04','spine_05','clavicle_l','upperarm_l','lowerarm_l','hand_l','clavicle_r','upperarm_r','lowerarm_r','hand_r','thigh_l','calf_l','foot_l','ball_l','thigh_r','calf_r','foot_r','ball_r','neck_01','neck_02','head'] as const;
const LANDING_HANDOFF=.32;
/** Flight accents over the clean mixer pose. Ordinary running and powered
 * flight remain entirely authored by their original animation clips. */
export class CrystalTravelPose {
  glide=0;
  approach=0;
  private wasGrounded=true;
  private recorded=false;
  landingHandoff=0;
  readonly wingFrame:CrystalWingFlightFrame={rotation:new T.Quaternion(),offset:new T.Vector3(),weight:0};
  private readonly previous=LANDING_BONES.map(()=>new T.Quaternion());
  private readonly touchdown=LANDING_BONES.map(()=>new T.Quaternion());
  private readonly a=new T.Vector3();
  private readonly b=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly right=new T.Vector3();
  private readonly back=new T.Vector3();
  private readonly radialUp=new T.Vector3();
  private readonly lateral=new T.Vector3();
  private readonly axis=new T.Vector3();
  private readonly delta=new T.Quaternion();
  private readonly world=new T.Quaternion();
  private readonly parent=new T.Quaternion();
  private readonly basis=new T.Matrix4();
  private landingClip:T.AnimationClip|undefined;
  private readonly landingSamplers:{bone:T.Bone;interpolant:T.Interpolant}[]=[];
  /** A traversal catch owns the body; do not replay an earlier glide landing. */
  reset():void {
    this.glide=this.approach=this.landingHandoff=this.wingFrame.weight=0;
    this.recorded=false;this.wasGrounded=true;
  }
  private sampleLanding(clip:T.AnimationClip|undefined,bones:ReadonlyMap<string,T.Bone>,weight:number):void {
    if(clip!==this.landingClip){
      this.landingClip=clip;this.landingSamplers.length=0;
      for(const track of clip?.tracks??[]){
        const name=/^\.bones\[([^\]]+)\]\.quaternion$/.exec(track.name)?.[1];
        const bone=name?bones.get(name):undefined;
        if(bone&&LANDING_BONES.includes(name as typeof LANDING_BONES[number]))this.landingSamplers.push({bone,interpolant:track.createInterpolant()});
      }
    }
    // Scrub only the airborne preparation of Jetpack_Land_02. Never play its
    // planted impact while airborne. Height drives both time and blend, so a
    // skim, interrupted approach or late entry all have a valid complete pose.
    const time=T.MathUtils.lerp(.015,.09,weight);
    for(const {bone,interpolant}of this.landingSamplers){
      this.world.fromArray(interpolant.evaluate(time)).normalize();bone.quaternion.slerp(this.world,weight);
    }
  }

  update(dt:number,group:T.Object3D,bones:ReadonlyMap<string,T.Bone>,glide:number,_turnRate:number,climb:number,approach:number,grounded=false,landingClip?:T.AnimationClip):void {
    const ease=1-Math.exp(-10*Math.max(0,dt));
    if(grounded&&!this.wasGrounded&&this.approach>.1&&this.recorded){
      this.landingHandoff=LANDING_HANDOFF;
      for(let i=0;i<LANDING_BONES.length;i++)this.touchdown[i].copy(this.previous[i]);
    }
    this.wasGrounded=grounded;
    if(!grounded)this.landingHandoff=0;
    // Ground IK and the flight reach must not solve the same knee. Once contact
    // wins, blend the last finished air pose into the finished grounded pose.
    if(grounded){this.glide=0;this.approach=0;if(this.landingHandoff===0)this.wingFrame.weight=0;return;}
    // The rising-flight clips own the entire pose, powered or unpowered.
    const ascent=1-T.MathUtils.smoothstep(climb,3,9);
    this.glide+=(glide*ascent-this.glide)*ease;
    this.approach+=(approach-this.approach)*ease;
    this.wingFrame.weight=T.MathUtils.smoothstep(this.approach,0,.12);
    // As the shoulders recline, keep the horizontal fan just behind them so
    // the raised forearms travel in front of its leading edge.
    this.wingFrame.offset.set(0,0,.32*T.MathUtils.smoothstep(this.approach,0,.4));
    if(this.glide<.001&&this.approach<.001)return;
    const pelvis=bones.get('Hips'),chest=bones.get('Spine2');
    const left=bones.get('LeftArm'),right=bones.get('RightArm');
    if(!pelvis||!chest||!left||!right)return;
    group.updateWorldMatrix(true,true);
    group.getWorldQuaternion(this.world);
    this.radialUp.set(0,1,0).applyQuaternion(this.world);this.axis.set(1,0,0).applyQuaternion(this.world);
    this.lateral.copy(this.axis);
    // Keep the six flying modules in the ordinary glide frame while the body
    // swings beneath it. Store in avatar space so planet carry stays exact.
    const head=bones.get('Head');
    if(head&&this.wingFrame.weight>0){
      pelvis.getWorldPosition(this.a);head.getWorldPosition(this.b);
      this.up.subVectors(this.b,this.a).normalize();this.back.crossVectors(this.lateral,this.up).normalize();
      this.up.crossVectors(this.back,this.lateral).normalize();
      this.wingFrame.rotation.setFromRotationMatrix(this.basis.makeBasis(this.lateral,this.up,this.back));
      group.getWorldQuaternion(this.parent).invert();this.wingFrame.rotation.premultiply(this.parent);
    }
    pelvis.getWorldPosition(this.a);chest.getWorldPosition(this.b);this.up.subVectors(this.b,this.a).normalize();
    left.getWorldPosition(this.a);right.getWorldPosition(this.b);this.right.subVectors(this.b,this.a).normalize();
    this.back.crossVectors(this.right,this.up).normalize();this.right.crossVectors(this.up,this.back).normalize();
    for(const [side,arm]of [[-1,'LeftArm'],[1,'RightArm']]as const){
      // A small abduction in the AUTHORED torso frame: bank, elbow bend,
      // wrist pose and shoulder motion all remain supplied by the boost clip.
      this.delta.setFromAxisAngle(this.back,side*T.MathUtils.degToRad(9)*this.glide);
      this.rotate(bones.get(arm));
    }
    const brace=T.MathUtils.smootherstep(this.approach,0,.85);
    if(brace>.001)this.sampleLanding(landingClip,bones,brace);
    group.updateWorldMatrix(false,true);
  }
  /** Called after contact solving, so the touchdown crossfade has one owner. */
  finish(dt:number,bones:ReadonlyMap<string,T.Bone>,allowed:boolean):void {
    if(!allowed)this.landingHandoff=0;
    this.landingHandoff=Math.max(0,this.landingHandoff-dt);
    const weight=T.MathUtils.smootherstep(1-this.landingHandoff/LANDING_HANDOFF,0,1);
    if(this.wasGrounded)this.wingFrame.weight=1-weight;
    for(let i=0;i<LANDING_BONES.length;i++){
      const bone=bones.get(LANDING_BONES[i]);if(!bone)continue;
      if(this.landingHandoff>0){this.world.copy(bone.quaternion);bone.quaternion.slerpQuaternions(this.touchdown[i],this.world,weight);}
      this.previous[i].copy(bone.quaternion);
    }
    this.recorded=true;
  }
  private rotate(bone:T.Bone|undefined):void {
    if(!bone?.parent)return;
    bone.getWorldQuaternion(this.world).premultiply(this.delta);
    bone.parent.getWorldQuaternion(this.parent).invert();
    bone.quaternion.copy(this.parent).multiply(this.world);bone.updateWorldMatrix(false,true);
  }
}
